// WikiAccess.jsx — admin: grant a user per-department + per-folder Read/Write access.
// Writes user-subject rows to wiki_acl (department + folder scope). 'Inherit' = no explicit
// grant (falls back to their role); 'No access' explicitly revokes.
import React, { useEffect, useState, useCallback } from 'react';
import { Search, ChevronRight, ChevronDown, Folder, ShieldCheck, Save, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { useWikiApi } from '../../../hooks/useWiki';
import { WIKI_DEPTS, DEPT_BY_ID, deptColors } from '../../../lib/wikiConstants';

const LEVELS = [
    { v: 'inherit', label: 'Inherit' },
    { v: 'view', label: 'Read' },
    { v: 'edit', label: 'Write' },
    { v: 'admin', label: 'Admin' },
    { v: 'none', label: 'No access' },
];

function flattenFolders(nodes, depth = 0, out = []) {
    (nodes || []).forEach(n => {
        if (n.kind === 'folder') {
            out.push({ folder_id: n.folder_id, name: n.name, depth });
            flattenFolders(n.children, depth + 1, out);
        }
    });
    return out;
}

function LevelSelect({ value, onChange }) {
    return (
        <select value={value} onChange={(e) => onChange(e.target.value)}
            className="h-7 text-[11px] font-semibold rounded-md border border-stone-200 bg-white px-1.5 focus:outline-none focus:border-stone-400">
            {LEVELS.map(l => <option key={l.v} value={l.v}>{l.label}</option>)}
        </select>
    );
}

export default function WikiAccess() {
    const api = useWikiApi();
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState('');
    const [sel, setSel] = useState(null);          // selected user object
    const [levels, setLevels] = useState({});      // scopeKey -> level  (d:<dept> | f:<folderId>)
    const [folderDept, setFolderDept] = useState({}); // folderId -> deptId
    const [deptFolders, setDeptFolders] = useState({}); // deptId -> [{folder_id,name,depth}]
    const [expanded, setExpanded] = useState({});  // deptId -> bool
    const [saving, setSaving] = useState(false);

    useEffect(() => { api.adminUsers().then(d => setUsers(d.users || [])).catch(() => setUsers([])); }, [api]);

    const loadFolders = useCallback(async (deptId) => {
        if (deptFolders[deptId]) return;
        try {
            const t = await api.tree(deptId);
            const flat = flattenFolders(t.nodes || []);
            setDeptFolders(p => ({ ...p, [deptId]: flat }));
            setFolderDept(p => { const m = { ...p }; flat.forEach(f => { m[f.folder_id] = deptId; }); return m; });
        } catch { setDeptFolders(p => ({ ...p, [deptId]: [] })); }
    }, [api, deptFolders]);

    const selectUser = useCallback(async (u) => {
        setSel(u); setLevels({}); setExpanded({});
        if (u.is_admin || u.is_super_admin) return; // global admins already have full access
        try {
            const d = await api.getUserAcl(u.user_id);
            const lv = {}; const fd = {}; const exp = {};
            (d.grants || []).forEach(g => {
                if (g.scope === 'department') lv[`d:${g.scope_id}`] = g.level;
                else if (g.scope === 'folder') { lv[`f:${g.scope_id}`] = g.level; if (g.dept) { fd[g.scope_id] = g.dept; exp[g.dept] = true; } }
            });
            setLevels(lv); setFolderDept(fd);
            // auto-load folders for depts that already have folder grants
            Object.keys(exp).forEach(dep => { setExpanded(p => ({ ...p, [dep]: true })); loadFolders(dep); });
        } catch { toast.error('Could not load user access'); }
    }, [api, loadFolders]);

    const setLevel = (key, val) => setLevels(p => ({ ...p, [key]: val }));
    const toggleDept = (deptId) => { setExpanded(p => ({ ...p, [deptId]: !p[deptId] })); if (!expanded[deptId]) loadFolders(deptId); };

    const save = async () => {
        if (!sel) return;
        setSaving(true);
        try {
            const entries = [];
            Object.entries(levels).forEach(([key, val]) => {
                if (val === 'inherit') return;
                const [k, id] = [key.slice(0, 1), key.slice(2)];
                if (k === 'd') entries.push({ scope: 'department', scope_id: id, level: val, dept: id });
                else entries.push({ scope: 'folder', scope_id: id, level: val, dept: folderDept[id] });
            });
            await api.setUserAcl(sel.user_id, { entries });
            toast.success(`Saved access for ${sel.name || sel.email}`);
        } catch { toast.error('Could not save access'); }
        finally { setSaving(false); }
    };

    const filtered = users.filter(u => !q || (u.name || '').toLowerCase().includes(q.toLowerCase()) || (u.email || '').toLowerCase().includes(q.toLowerCase()));

    return (
        <div className="h-full flex">
            {/* user list */}
            <div className="w-[280px] border-r border-stone-200 bg-white flex flex-col">
                <div className="p-3 border-b border-stone-200">
                    <div className="relative">
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…"
                            className="w-full h-8 pl-8 pr-3 rounded-lg border border-stone-200 text-[12px] focus:outline-none focus:border-stone-400" />
                        <Search className="w-3.5 h-3.5 text-stone-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    </div>
                </div>
                <div className="flex-1 overflow-auto p-2 space-y-0.5">
                    {filtered.map(u => (
                        <button key={u.user_id} onClick={() => selectUser(u)}
                            className={`w-full text-left px-2 py-1.5 rounded-lg ${sel?.user_id === u.user_id ? 'bg-stone-100' : 'hover:bg-stone-50'}`}>
                            <div className="text-[12.5px] font-medium text-stone-900 truncate flex items-center gap-1.5">{u.name || u.email}{(u.is_admin || u.is_super_admin) && <span className="text-[9px] font-semibold px-1 rounded bg-stone-200 text-stone-600">ADMIN</span>}</div>
                            <div className="text-[10.5px] text-stone-500 truncate">{u.email}</div>
                        </button>
                    ))}
                    {filtered.length === 0 && <div className="text-[12px] text-stone-400 px-2 py-3">No people.</div>}
                </div>
            </div>

            {/* grant editor */}
            <div className="flex-1 overflow-auto">
                {!sel ? (
                    <div className="h-full flex items-center justify-center text-center text-stone-400">
                        <div><UserCog className="w-8 h-8 mx-auto mb-2 text-stone-300" /><div className="text-[13px]">Select a person to manage their Wiki access.</div></div>
                    </div>
                ) : (sel.is_admin || sel.is_super_admin) ? (
                    <div className="p-6 max-w-[680px]">
                        <div className="text-[15px] wiki-display font-bold text-stone-900">{sel.name || sel.email}</div>
                        <div className="mt-3 rounded-xl border border-stone-200 bg-stone-50 p-4 text-[13px] text-stone-600 inline-flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4 text-emerald-600" /> This person is a workspace admin — they already have full access to every department.
                        </div>
                    </div>
                ) : (
                    <div className="p-5 max-w-[760px]">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="text-[15px] wiki-display font-bold text-stone-900 flex-1">{sel.name || sel.email}</div>
                            <button onClick={save} disabled={saving} className="h-9 px-3.5 rounded-lg bg-stone-900 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"><Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save access'}</button>
                        </div>
                        <div className="text-[12px] text-stone-500 mb-4">Set department access, or expand a department to grant access to specific folders. <b>Inherit</b> keeps their role-based default; <b>No access</b> explicitly blocks.</div>

                        <div className="space-y-2">
                            {WIKI_DEPTS.map(d => {
                                const Ico = DEPT_BY_ID[d.id]?.icon; const c = deptColors(d.id);
                                const folders = deptFolders[d.id] || [];
                                const open = !!expanded[d.id];
                                return (
                                    <div key={d.id} className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                                        <div className="flex items-center gap-2.5 px-3 py-2.5">
                                            <button onClick={() => toggleDept(d.id)} className="text-stone-400 hover:text-stone-700">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
                                            <div className={`w-7 h-7 rounded-md flex items-center justify-center ${c.tile}`}>{Ico && <Ico className="w-3.5 h-3.5" />}</div>
                                            <span className="text-[13px] font-semibold text-stone-900 flex-1">{d.name}</span>
                                            <LevelSelect value={levels[`d:${d.id}`] || 'inherit'} onChange={(v) => setLevel(`d:${d.id}`, v)} />
                                        </div>
                                        {open && (
                                            <div className="border-t border-stone-100 bg-stone-50/50 px-3 py-2 space-y-1">
                                                {folders.length === 0 && <div className="text-[11px] text-stone-400 py-1">No folders in this department.</div>}
                                                {folders.map(f => (
                                                    <div key={f.folder_id} className="flex items-center gap-2" style={{ paddingLeft: 8 + f.depth * 16 }}>
                                                        <Folder className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                                        <span className="text-[12px] text-stone-700 flex-1 truncate">{f.name}</span>
                                                        <LevelSelect value={levels[`f:${f.folder_id}`] || 'inherit'} onChange={(v) => setLevel(`f:${f.folder_id}`, v)} />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
